import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import deepmerge from 'deepmerge';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareLogEntries = (e1, e2) => e1.id === e2.id;

const logSlice = createSlice({
    name: 'log',
    initialState: {
        data: []
    },
    reducers: {
        addOrUpdate: (state, action) => {
            const entries = ensureArray(action.payload);
            if (entries.length < 1000) {
                entries.forEach(entry => {
                    let idx = state.data.findIndex(e => compareLogEntries(e, entry));
                    if (idx >= 0) {
                        state.data.splice(idx, 1, entry);
                    } else {
                        state.data.push(entry);
                    }
                })
            } else {
                const lookupMap = new Map(state.data.map(entry => [entry.id, entry]));

                entries.forEach(entry => {
                    if (lookupMap.has(entry.id)) {
                        deepmerge(lookupMap.get(entry.id), entry);
                    } else {
                        lookupMap.set(entry.id, entry);
                    }
                });

                state.data = Array.from(lookupMap.values());
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => logSlice.getInitialState());
    }
});

// #region network operations
const fetchLogEntires = (site, level, type) => network => {
    let query = {}
    if (site) {
        query.site = site;
    }
    if (level) {
        query.level = level;
    }
    if (type) {
        query.type = type;
    }
    network.post('/api/v1/logs', query, logSlice.actions.addOrUpdate);
}

export const createLogEntry = (network, entry) => network.post('/api/v1/log', entry, logSlice.actions.addOrUpdate);
export const strikeLogEntry = (network, entry) => network.put('/api/v1/log', entry, logSlice.actions.addOrUpdate);
// #endregion

// #region permission checks
export const canReadLog = (credentials, { site, level, type}) => credentials.log.has({ site, level, type });
export const canModifyLog = (credentials, { site, level, type }) => credentials.log.hasFull({ site, level, type });
// #endregion

// #region selectors
const selectAllData = state => state.log.data;

const selectEntiresBySiteLevelType = createSelector([selectAllData, (data, site) => site, (data, site, level) => level, (data, site, level, type) => type], (data, site, level, type) => {
    return data.filter(entry => (!site || !entry.site || entry.site === site) && (!level || !entry.level || entry.level === level) && (!type || !entry.type || entry.type === type));
});
const selectLogEntires = (site, level, type) => state => selectEntiresBySiteLevelType(state, site, level, type);
// #endregion

// #region listeners
const listen = {
    logCreated: (entry, { dispatch }) => dispatch(logSlice.actions.addOrUpdate(entry)),
    logStruck: (entry, { dispatch }) => dispatch(logSlice.actions.addOrUpdate(entry))
}
// #endregion

export const [useLogEntries] = dataHookFactory(selectLogEntires, initMapFactory(fetchLogEntires, listen, { allowEmpty: true }));

const reducerDetails = { name: logSlice.name, reducer: logSlice.reducer };
export default reducerDetails;